import React, { useState, useEffect, useCallback } from 'react';
import {
  C, S, Badge, StatusBadge, FormField, ErrList, StatCard,
  PageHeader, FilterBar, DataTable, Modal, FormGrid,
  useToast, useConfirm, useCRUD,
} from '../../components/inventoryShared';
import {
  categoryController, subCategoryController, brandController,
  unitController, productController, warehouseController,
  locationController, supplierController, purchaseOrderController,
  purchaseReturnController, stockInController, stockOutController,
  stockTransferController, stockAdjustmentController,
  stockController, reportController,
} from '../../controllers/inventoryController';
import ActionBtns from './ActionBtns';
import FormModal from './FormModal';
import {
  Tag, Layers, Boxes, Settings, Package, BarChart3,
  PackageCheck, TrendingDown, ArrowLeftRight, ClipboardList,
  Users, ShoppingCart, RotateCcw, Warehouse, MapPin,
  AlertTriangle, Receipt, FileText, QrCode, Plus,
  Edit2, Trash2, Eye, Download, RefreshCw, CheckCircle,
  XCircle, Truck, ChevronDown, ChevronUp, Search,
} from 'lucide-react';
// ─────────────────────────────────────────────────────────────────────────────
// 6. WAREHOUSES PAGE
// ─────────────────────────────────────────────────────────────────────────────
export const WarehousesPage = ({ companyId }) => {
  const crud = useCRUD(warehouseController, companyId);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [errors, setErrors]     = useState([]);
  const emptyForm = { name:'',code:'',address:'',city:'',state:'',pincode:'',phone:'',email:'',managerName:'',managerPhone:'',capacity:0,type:'Main',status:'Active',notes:'' };
  const [form, setForm] = useState(emptyForm);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const openAdd  = () => { setEditItem(null); setForm(emptyForm); setErrors([]); setShowForm(true); };
  const openEdit = (r) => { setEditItem(r); setForm({...r}); setErrors([]); setShowForm(true); };
  const handleSave = async () => {
    setSaving(true); setErrors([]);
    const res = editItem ? await warehouseController.update(editItem.id,{...form,companyId}) : await warehouseController.create({...form,companyId});
    setSaving(false);
    if (res.success) { crud.show(res.message); setShowForm(false); crud.fetchAll(); }
    else setErrors(res.errors?.length?res.errors:[res.message]);
  };

  const columns = [
    { label:'Code', key:'code', render: v => <span style={{ fontFamily:'monospace', fontSize:12, color:C.primary }}>{v||'—'}</span> },
    { label:'Warehouse', key:'name', render: v => <div style={{ fontWeight:700 }}>{v}</div> },
    { label:'Type', key:'type', render: v => <Badge color={C.purple} bg={`${C.purple}15`}>{v}</Badge> },
    { label:'City', key:'city', render: v => v||'—' },
    { label:'Manager', key:'managerName', render: (v,r) => <div><div style={{ fontWeight:600 }}>{v||'—'}</div><div style={{ fontSize:11, color:C.textMuted }}>{r.managerPhone}</div></div> },
    { label:'Capacity', key:'capacity', render: (v,r) => v ? <div><div style={{ fontSize:12 }}>{r.usedCapacity||0}/{v}</div><div style={{ height:4, background:'#e5e7eb', borderRadius:2, marginTop:3, overflow:'hidden' }}><div style={{ height:'100%', width:`${Math.min(100,Math.round(((r.usedCapacity||0)/v)*100))}%`, background:C.primary, borderRadius:2 }}/></div></div> : '—' },
    { label:'Status', key:'status', render: v => <StatusBadge status={v}/> },
    { label:'', key:'actions', render:(_, r) => <ActionBtns onEdit={()=>openEdit(r)} onDelete={()=>crud.handleDelete(r.id,`"${r.name}"`)} /> },
  ];

  return (
    <div style={S.page}>
      {crud.ToastEl}{crud.Dialog}
      <PageHeader title="Warehouses" subtitle="Manage warehouse locations" Icon={Warehouse} color={C.primary}
        action={<button style={S.btnPri} onClick={openAdd}><Plus size={15}/> Add Warehouse</button>} />
      <div style={S.card}>
        <div style={{ padding:'14px 14px 0' }}><FilterBar search={crud.search} onSearch={crud.setSearch} /></div>
        <DataTable columns={columns} data={crud.data} loading={crud.loading} emptyTitle="No warehouses found"
          page={crud.page} totalPages={crud.totalPages} onPageChange={crud.setPage} total={crud.total} />
      </div>
      {showForm && (
        <FormModal title={editItem?'Edit Warehouse':'Add Warehouse'} onClose={()=>setShowForm(false)} onSave={handleSave} saving={saving} maxWidth={720}>
          <ErrList errors={errors} />
          <FormGrid>
            <FormField label="Warehouse Name *"><input style={S.input} value={form.name} onChange={e=>set('name',e.target.value)} /></FormField>
            <FormField label="Code *"><input style={S.input} value={form.code} onChange={e=>set('code',e.target.value)} /></FormField>
            <FormField label="Type"><select style={S.select} value={form.type} onChange={e=>set('type',e.target.value)}>{['Main','Branch','Transit','Cold Storage','Bonded'].map(t=><option key={t}>{t}</option>)}</select></FormField>
            <FormField label="Capacity (units)"><input type="number" style={S.input} value={form.capacity} onChange={e=>set('capacity',e.target.value)} /></FormField>
            <FormField label="Phone"><input style={S.input} value={form.phone} onChange={e=>set('phone',e.target.value)} /></FormField>
            <FormField label="Email"><input style={S.input} value={form.email} onChange={e=>set('email',e.target.value)} /></FormField>
            <FormField label="Manager Name"><input style={S.input} value={form.managerName} onChange={e=>set('managerName',e.target.value)} /></FormField>
            <FormField label="Manager Phone"><input style={S.input} value={form.managerPhone} onChange={e=>set('managerPhone',e.target.value)} /></FormField>
            <FormField label="Address" col={2}><input style={S.input} value={form.address} onChange={e=>set('address',e.target.value)} /></FormField>
            <FormField label="City"><input style={S.input} value={form.city} onChange={e=>set('city',e.target.value)} /></FormField>
            <FormField label="State"><input style={S.input} value={form.state} onChange={e=>set('state',e.target.value)} /></FormField>
            <FormField label="Pincode"><input style={S.input} value={form.pincode} onChange={e=>set('pincode',e.target.value)} /></FormField>
            <FormField label="Status"><select style={S.select} value={form.status} onChange={e=>set('status',e.target.value)}><option>Active</option><option>Inactive</option></select></FormField>
            <FormField label="Notes" col={2}><textarea style={S.textarea} value={form.notes} onChange={e=>set('notes',e.target.value)} /></FormField>
          </FormGrid>
        </FormModal>
      )}
    </div>
  );
};
export default WarehousesPage;